import { useState, useEffect } from 'react'
import { getAllPostsAdmin, type BlogPost } from '../lib/blog'

export default function AdminBlog() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [editing, setEditing] = useState<BlogPost | null>(null)
  const [form, setForm] = useState({ title: '', author: 'Matt Miesnieks', status: 'draft' as 'draft' | 'published', date: new Date().toISOString().split('T')[0], tags: '', excerpt: '', content: '' })
  const [copied, setCopied] = useState(false)
  const [showExport, setShowExport] = useState(false)

  useEffect(() => {
    getAllPostsAdmin().then(setPosts)
  }, [])

  function startNew() {
    setEditing(null)
    setForm({ title: '', author: 'Matt Miesnieks', status: 'draft', date: new Date().toISOString().split('T')[0], tags: '', excerpt: '', content: '' })
  }

  function startEdit(post: BlogPost) {
    setEditing(post)
    setForm({ title: post.title, author: post.author, status: post.status, date: post.date, tags: post.tags.join(', '), excerpt: post.excerpt, content: post.content })
  }

  function buildSlug(title: string) {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

  function buildMarkdown() {
    const slug = editing?.slug ?? buildSlug(form.title)
    const initials = form.author.split(' ').map(w => w[0]).join('')
    const tagsArr = form.tags.split(',').map(t => t.trim()).filter(Boolean)
    return `---\ntitle: "${form.title}"\nslug: "${slug}"\nauthor: "${form.author}"\nauthorInitials: "${initials}"\ndate: "${form.date}"\nreadTime: "5 min read"\ntags: ${JSON.stringify(tagsArr)}\nstatus: "${form.status}"\nexcerpt: "${form.excerpt}"\n---\n\n${form.content}`
  }

  function handleExport() {
    setShowExport(true)
    setCopied(false)
  }

  function handleCopy() {
    navigator.clipboard.writeText(buildMarkdown())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const slug = editing?.slug ?? buildSlug(form.title)
  const filepath = `src/content/posts/${slug || 'your-slug'}.md`

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-h3 text-chalk">Blog Posts</h1>
          <p className="text-body-sm text-steel mt-1">{posts.length} posts · {posts.filter(p => p.status === 'published').length} published · {posts.filter(p => p.status === 'draft').length} drafts</p>
        </div>
        <button onClick={startNew} className="bg-signal hover:bg-signal-hover text-white text-body-sm font-semibold px-4 py-2 rounded transition-colors">
          + New Post
        </button>
      </div>

      <div className="grid grid-cols-[1fr_1fr] gap-8 tablet:grid-cols-1">
        {/* Left: posts list + editor */}
        <div>
          {/* Posts table */}
          <div className="bg-obsidian border border-carbon rounded overflow-hidden mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-carbon">
                  <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Title</th>
                  <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Author</th>
                  <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Status</th>
                  <th className="text-left text-subhead uppercase tracking-wider text-steel px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => (
                  <tr key={post.slug} className="border-b border-carbon/50 hover:bg-carbon/20 transition-colors">
                    <td className="px-4 py-3 text-body-sm text-chalk font-medium truncate max-w-[200px]">{post.title}</td>
                    <td className="px-4 py-3 text-body-sm text-steel">{post.author.split(' ')[0]}</td>
                    <td className="px-4 py-3">
                      {post.status === 'published'
                        ? <span className="text-caption-1 font-semibold px-2 py-0.5 rounded-full bg-success-dark/15 text-success-dark">Published</span>
                        : <span className="text-caption-1 font-semibold px-2 py-0.5 rounded-full bg-carbon text-steel">Draft</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-body-sm text-steel">{post.date}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => startEdit(post)} className="text-body-sm text-signal hover:text-signal-hover mr-3 transition-colors">Edit</button>
                    </td>
                  </tr>
                ))}
                {posts.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-body-sm text-steel">No posts yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Editor */}
          <div className="bg-obsidian border border-carbon rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-title-2 text-chalk">{editing ? 'Edit Post' : 'New Post'}</h2>
              <div className="flex gap-2">
                <button onClick={handleExport} className="bg-carbon hover:bg-carbon/70 text-chalk text-body-sm font-semibold px-3 py-1.5 rounded transition-colors">
                  Export markdown
                </button>
              </div>
            </div>

            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Post title…"
              className="w-full bg-void border border-carbon rounded px-4 py-2.5 text-title-3 text-chalk font-semibold placeholder:text-steel focus:outline-none focus:border-signal mb-3"
            />

            <div className="grid grid-cols-3 gap-3 mb-3">
              <select value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                <option>Matt Miesnieks</option>
                <option>Mehdi Nikkhah</option>
              </select>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as 'draft' | 'published' }))} className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal" />
            </div>

            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)…" className="w-full bg-void border border-carbon rounded px-4 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal mb-3" />
            <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Excerpt (1–2 sentences)…" rows={2} className="w-full bg-void border border-carbon rounded px-4 py-2 text-body-sm text-chalk placeholder:text-steel focus:outline-none focus:border-signal mb-3 resize-none" />

            <div className="flex gap-1 mb-1">
              {['**B**', '_I_', '## H2', '### H3', '[link](url)', '`code`'].map(btn => (
                <button key={btn} onClick={() => setForm(f => ({ ...f, content: f.content + btn }))} className="bg-carbon hover:bg-carbon/70 text-steel text-caption-1 font-semibold px-2 py-1 rounded transition-colors">{btn}</button>
              ))}
            </div>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Write your post in markdown…" rows={12} className="w-full bg-void border border-carbon rounded px-4 py-2.5 text-body-sm text-chalk font-mono placeholder:text-steel focus:outline-none focus:border-signal resize-y" />
          </div>
        </div>

        {/* Right: live preview */}
        <div className="bg-obsidian border border-carbon rounded p-6 sticky top-6 self-start">
          <h2 className="text-title-2 text-chalk mb-4">Preview</h2>
          {form.title ? <h3 className="text-title-1 text-pure font-semibold mb-2 leading-snug">{form.title}</h3> : <p className="text-body-sm text-steel mb-2">Title will appear here</p>}
          {form.excerpt && <p className="text-body-sm text-steel mb-4 italic">{form.excerpt}</p>}
          <div className="prose-dark text-body-sm text-steel leading-relaxed whitespace-pre-wrap border-t border-carbon pt-4 max-h-[500px] overflow-y-auto">
            {form.content || <span className="italic">Content will appear here…</span>}
          </div>
        </div>
      </div>

      {/* Export modal */}
      {showExport && (
        <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-obsidian border border-carbon rounded p-8 max-w-2xl w-full">
            <h2 className="text-title-1 text-chalk font-semibold mb-2">Save this file to publish</h2>
            <p className="text-body-sm text-steel mb-1">Copy the markdown below and save it as:</p>
            <code className="text-body-sm text-signal font-mono block mb-4">{filepath}</code>
            <textarea readOnly value={buildMarkdown()} rows={12} className="w-full bg-void border border-carbon rounded px-4 py-3 text-body-sm text-chalk font-mono resize-none focus:outline-none mb-4" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowExport(false)} className="text-body-sm text-steel hover:text-chalk px-4 py-2 rounded border border-carbon transition-colors">Close</button>
              <button onClick={handleCopy} className="bg-signal hover:bg-signal-hover text-white text-body-sm font-semibold px-4 py-2 rounded transition-colors">
                {copied ? '✓ Copied!' : 'Copy to clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
